import {
  BarChart3,
  Globe,
  Home,
  Images,
  type LucideIcon,
  Mail,
  MessageSquare,
  Palette,
  Plug,
  Repeat,
  Settings,
  Users,
  Workflow,
} from 'lucide-react'
import type { Messages } from '@/lib/i18n/messages'

/** A nav item's name, under `appShell.nav` in the reader's language. */
export type AppNavLabelKey = keyof Messages['appShell']['nav']

export type AppNavItem = {
  href: string
  icon: LucideIcon
  /** Its name: `nav.<labelKey>` in the `appShell` catalog. */
  labelKey: AppNavLabelKey
  /** Optional override for prefix matching (e.g. the Settings link points at
   *  /settings/team but should highlight on any /settings/* path). */
  matchPrefix?: string
  /** Optional additional prefixes that should also mark the item as active.
   *  Used when one sidebar item represents multiple URL roots (e.g. Emails
   *  highlights for both /emails/* and /automations/*). */
  matchPrefixes?: ReadonlyArray<string>
}

/**
 * The sidebar "Chats" tab. Its click is mode-aware (see `ChatsNavItem` in
 * `app-sidebar.tsx`): with an open side panel it switches that panel to the
 * recent-chats overview in place; otherwise it navigates to the full-width
 * `/chats` surface. Active on both full chat surfaces (`/chats`, `/chat/*`).
 */
export const APP_NAV_CHATS: AppNavItem = {
  href: '/chats',
  icon: MessageSquare,
  labelKey: 'chats',
  matchPrefixes: ['/chat'],
}

export const APP_NAV_PRIMARY: ReadonlyArray<AppNavItem> = [
  {
    href: '/home',
    icon: Home,
    labelKey: 'home',
    matchPrefixes: ['/home', '/usecases'],
  },
  APP_NAV_CHATS,
  {
    href: '/emails',
    icon: Mail,
    labelKey: 'emails',
  },
  {
    href: '/automations',
    icon: Workflow,
    labelKey: 'automations',
    matchPrefixes: ['/triggers'],
  },
  // `/flows` is the gallery's Flows pill on its own route.
  {
    href: '/templates',
    icon: Repeat,
    labelKey: 'templates',
    matchPrefixes: ['/flows'],
  },
]

export const APP_NAV_MANAGE: ReadonlyArray<AppNavItem> = [
  {
    href: '/brand',
    icon: Palette,
    labelKey: 'brand',
    // `/brand` is a real page (Details); the href's own prefix match keeps
    // the row active on its three sibling tabs and excludes `/brand-extraction`.
  },
  { href: '/assets', icon: Images, labelKey: 'assets' },
  { href: '/domains', icon: Globe, labelKey: 'domains' },
  { href: '/audience', icon: Users, labelKey: 'audience' },
  {
    href: '/analytics',
    icon: BarChart3,
    labelKey: 'analytics',
    matchPrefixes: ['/analytics', '/events'],
  },
  { href: '/integrations', icon: Plug, labelKey: 'integrations' },
]

export const APP_NAV_SETTINGS: AppNavItem = {
  href: '/settings/team',
  icon: Settings,
  labelKey: 'settings',
  matchPrefix: '/settings',
}

const ALL_NAV_ITEMS: ReadonlyArray<AppNavItem> = [
  ...APP_NAV_PRIMARY,
  ...APP_NAV_MANAGE,
  APP_NAV_SETTINGS,
]

/**
 * True on the signed-in landing page. `proxy.ts` redirects `/` → `/home` for
 * a signed-in org, so `/home` is what `usePathname()` normally sees; `/` is
 * kept as a second spelling of the same page (it is what the pre-redirect
 * rewrite produced, and every other landing predicate still honours it).
 */
export function isHomePath(pathname: string | null | undefined): boolean {
  return pathname === '/home' || pathname === '/'
}

export function isAppNavItemActive(
  pathname: string,
  item: AppNavItem
): boolean {
  if (item.href === '/home') {
    return isHomePath(pathname)
  }
  const base = item.matchPrefix ?? item.href
  const candidates = [base, ...(item.matchPrefixes ?? [])]
  return candidates.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/**
 * Returns the active top-level nav item for the given pathname, or null
 * if no nav item matches. Used by the app header to render a default
 * page title when the current page hasn't set its own.
 */
export function getActiveAppNavItem(pathname: string): AppNavItem | null {
  for (const item of ALL_NAV_ITEMS) {
    if (isAppNavItemActive(pathname, item)) {
      return item
    }
  }
  return null
}
